import styled from 'styled-components';

import { gridColorSelector } from '@/components/v2/GridLines';
import { styles } from '@/components/v2/UtilityCss/Text';
import { ReactComponent as DynamicIcon } from '@/images/icons/icon-dynamic.svg';
import { ReactComponent as LockIcon } from '@/images/icons/icon-lock.svg';
import { ReactComponent as MagicIcon } from '@/images/icons/icon-magic.svg';
import { ReactComponent as SelectCellIcon } from '@/images/icons/icon-select-cell.svg';

import { generateFluidPxScale } from '../blog-2/utils';

import { CustomerStoryPopover } from './CustomerBanner';

const Popover = styled(CustomerStoryPopover)`
  left: 50%;
  transform: translate(-50%, 16px);
  bottom: 100%;
  padding: 8px 16px;
  width: fit-content;

  @media (max-width: ${({ theme }) => theme.mediaQuery.MEDIUM}) {
    display: none;
  }
`;

const Description = styled.p`
  width: 22ch;
`;

const CAPABILITIES = [
  {
    title: 'Magic AI',
    icon: <MagicIcon />,
    description: (
      <>
        Go from question to <br />
        answer in seconds using our conversational AI interface.
      </>
    )
  },
  {
    title: 'Collaboration',
    icon: <SelectCellIcon />,
    description: (
      <>
        Edit and comment in real-time, endorse trusted data, and build with
        reusable components.
      </>
    )
  },
  {
    title: 'Integration',
    icon: <DynamicIcon />,
    description: (
      <>
        Hit the ground running with connections to popular data warehouses,
        flexible APIs, and semantic model integrations.
      </>
    )
  },
  {
    title: 'Security',
    icon: <LockIcon />,
    description: (
      <>
        Built on enterprise-grade security and industry-leading privacy
        standards keeping our customer’s data secure.
      </>
    )
  }
];

const Row = styled.div`
  display: grid;
  grid-template-columns: repeat(${CAPABILITIES.length}, min-content);
  gap: ${generateFluidPxScale(56, 100)};
  padding: 48px 0;
  align-items: center;
  margin-bottom: 1rem;
`;

const ThroughLine = styled.div`
  grid-row: 1 / -1;
  grid-column: 1 / -1;
  height: 1px;
  border-bottom: 1px dashed ${({ theme }) => gridColorSelector(theme, 'major')};
`;

const RowSubgrid = styled.div`
  grid-row: 1 / -1;
  grid-column: 1 / -1;
  display: grid;
  grid-template-columns: subgrid;
`;

const Capability = styled.div`
  padding: 12px;
  background-color: ${({ theme }) => theme.palette.fg100};
  position: relative;
  grid-row: 1 / -1;
  cursor: help;

  @media (hover: hover) {
    &:hover ${Popover} {
      opacity: 1;
      transform: translate(-50%, 0);
    }
  }

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    background-image: ${({ theme }) => theme.textures.noise.img};
    background-size: ${({ theme }) => theme.textures.noise.size};
    mix-blend-mode: ${({ theme }) => theme.textures.noise.mixBlendMode};
    opacity: ${({ theme }) => theme.textures.noise.opacity};
    pointer-events: none;
  }
`;

const Title = styled.div`
  text-align: center;
  white-space: nowrap;
  position: absolute;
  left: 50%;
  bottom: 0;
  transform: translate(-50%, 50%);
  font-size: ${({ theme }) => theme.fontSize.EXTRA_SMALL};
  color: ${({ theme }) => theme.palette.fg1100};
`;

export function PlatformCapabilities() {
  return (
    <Row>
      <ThroughLine />
      <RowSubgrid>
        {CAPABILITIES.map(c => (
          <Capability key={c.title}>
            <Popover>
              <Description className={styles.s}>{c.description}</Description>
            </Popover>
            {c.icon}
            <Title className={styles.annotation}>{c.title}</Title>
          </Capability>
        ))}
      </RowSubgrid>
    </Row>
  );
}
